import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { restrictedZIndexes } from '../../utils/restricted_z_indexes';
import { useCellulaProviderContext } from '../provider';
import { FileViewerContext } from './_file_viewer.context';
import { CellulaFileViewerActionBar } from './_file_viewer_action_bar.component';
import { CellulaFileViewerList } from './_file_viewer_list.component/mod';
import { CellulaFileViewerTopBar } from './_file_viewer_top_bar.component';
import type { FileWorkerStaleDuckType } from './_file_worker_stale_duck_type';
import { theme } from './file_viewer.theme';

const elements = {
	background: styled(motion.div)`
		background: ${theme.getValue('background')};
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
	`,
	fader: styled(motion.div)`
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: ${restrictedZIndexes.DANGER};
	`,
	container: styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		color: ${theme.getValue('textColor')};
		padding-top: calc(${coreTokens.spacing.x1_5} + ${coreTokens.spacing.x6});
		padding-bottom: calc(${coreTokens.spacing.x2} * 2 + ${coreTokens.spacing.x5});

		${coreTokens.breakpointQuery.small} {
			padding-top: calc(${coreTokens.spacing.x2} + ${coreTokens.spacing.x6});
			padding-bottom: calc(${coreTokens.spacing.x3} * 2 + ${coreTokens.spacing.x5});
		}
	`,
};

function Background(): JSX.Element {
	const { y } = useContext(FileViewerContext);
	const opacity = useTransform(y, [-innerHeight * 2, 0, innerHeight * 2], [0, 1, 0]);

	return <elements.background style={{ opacity }} aria-hidden />;
}

export interface CellulaFileViewerRootProps {
	onDownloadError?: () => void;
	defaultOpen?: boolean;
	defaultStartIndex?: number;
	showDownload?: boolean;
	children?: ReactNode | ReactNode[];
	onClose?: () => void;
}

export function CellulaFileViewerRoot({
	onDownloadError,
	defaultOpen = false,
	showDownload = true,
	defaultStartIndex = 0,
	onClose,
	children,
}: CellulaFileViewerRootProps): JSX.Element {
	const { portalElement } = useCellulaProviderContext();
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [showBar, setShowBar] = useState(true);
	const [showBarManually, setShowBarManually] = useState(true);
	const [startIndex, setStartIndex] = useState(defaultStartIndex);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [hasDisabledScroll, setHasDisabledScroll] = useState(false);
	const [files, setFiles] = useState<FileWorkerStaleDuckType[]>([]);
	const y = useMotionValue(0);

	// useEffect(function resetBodyWhenFileViewerUnmounts() {
	// 	const snapShotDocumentElementDefault = document.documentElement.style.backgroundColor;
	// 	const snapShotBodyDefault = document.body.style.backgroundColor;

	// 	return () => {
	// 		document.documentElement.style.backgroundColor = snapShotDocumentElementDefault;
	// 		document.body.style.backgroundColor = snapShotBodyDefault;
	// 	};
	// }, []);

	useEffect(
		function listenToYChanges() {
			return y.on('change', () => {
				// TODO update statusbar
			});
		},
		[y],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenIsOpenChanges() {
			// const _ = document.querySelector('meta[name="theme-color"]')?.getAttribute('content') ?? '#fff';

			// if (isOpen) {
			// 	document.documentElement.style.backgroundColor = '#000';
			// 	document.body.style.backgroundColor = '#000';
			// 	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#000');
			// } else {
			// 	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#fff');
			// 	document.body.style.backgroundColor = '#fff';
			// 	document.documentElement.style.backgroundColor = '#fff';
			// }

			setShowBar(isOpen);
			setShowBarManually(isOpen);

			if (!isOpen) {
				onClose?.();
			}
		},
		[isOpen],
	);

	useEffect(function bindGlobalEventListeners() {
		function handleKeypress(event: KeyboardEvent): void {
			switch (event.key) {
				case 'Escape':
				case 'Esc':
					setIsOpen(false);
					break;
			}
		}

		globalThis.addEventListener('keyup', handleKeypress);

		return () => {
			globalThis.removeEventListener('keyup', handleKeypress);
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	const goToPrevious = useCallback(() => {
		const maxIndex = files.length - 1;
		const prev = currentIndex - 1;

		setCurrentIndex(prev < 0 ? maxIndex : prev);
	}, [files, currentIndex, setCurrentIndex]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	const goToNext = useCallback(() => {
		const maxIndex = files.length - 1;
		const next = currentIndex + 1;

		setCurrentIndex(next > maxIndex ? 0 : next);
	}, [files, currentIndex, setCurrentIndex]);

	function disableRadixAutoFocusOnOpen(event: Event): void {
		event.preventDefault();
	}

	return (
		<FileViewerContext.Provider
			value={{
				isOpen,
				setIsOpen,
				files,
				setFiles,
				hasDisabledScroll,
				setHasDisabledScroll,
				startIndex,
				setStartIndex,
				showBar,
				setShowBar,
				showBarManually,
				setShowBarManually,
				showDownload,
				currentIndex,
				setCurrentIndex,
				y,
				onDownloadError,
				goToPrevious,
				goToNext,
			}}
		>
			{children}
			<DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
				<AnimatePresence>
					{isOpen && files.length ? (
						<DialogPrimitive.Portal container={portalElement} forceMount>
							<DialogPrimitive.Overlay>
								<DialogPrimitive.Content onOpenAutoFocus={disableRadixAutoFocusOnOpen}>
									<elements.fader
										transition={{
											ease: 'linear',
										}}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{
											opacity: 0,
										}}
									>
										<elements.container>
											<Background />
											<CellulaFileViewerTopBar />
											<CellulaFileViewerList />
											<CellulaFileViewerActionBar />
										</elements.container>
									</elements.fader>
								</DialogPrimitive.Content>
							</DialogPrimitive.Overlay>
						</DialogPrimitive.Portal>
					) : null}
				</AnimatePresence>
			</DialogPrimitive.Root>
		</FileViewerContext.Provider>
	);
}
